import React, { useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom'; // add at top

import tpo from '../../../assets/HilliardPage/Commercial Roofing/TPO_Roofing_hilliard_ohio_ultimatesroofing.png';
import pvc from '../../../assets/HilliardPage/Commercial Roofing/PVC_Roofing_hilliard_ohio_Ultimatesroofing.jpg';
import epdm from '../../../assets/HilliardPage/Commercial Roofing/EPDM_Roofing_hilliard_ohio_ultimatesroofing.jpg';
import coatings from '../../../assets/HilliardPage/Commercial Roofing/Roof_Coatings_&_Maintenance_hilliard_ohio_ultimatesroofing.jpg';
import repairs from '../../../assets/HilliardPage/Commercial Roofing/Commercial_Roof_Repairs_hilliard_ohio_ultimatesroofing.jpg';
import metal from '../../../assets/HilliardPage/Commercial Roofing/Commercial_Metal_Roofing_hilliard_ohio_ultimatesroofing.jpg';

import '../all/Main.css';
import './RoofingSection.css';

import View from './View';
import Badge from '../ServiceSection/Badge';

const SERVICES = [
  {
    title: "TPO Roofing",
    description: "Energy-efficient TPO roofing for Hilliard businesses, reducing costs and boosting durability.",
    image: tpo,
    tag: "Commercial"
  },
  {
    title: "EPDM Roofing",
    description: "Long-lasting EPDM roofing systems ideal for commercial properties in Hilliard, Ohio.",
    image: epdm,
    tag: "Commercial"
  },
  {
    title: "PVC Roofing",
    description: "Premium PVC roofing offering superior leak resistance for Hilliard commercial buildings.",
    image: pvc,
    tag: "Commercial"
  },
  {
    title: "Roof Coatings & Maintenance",
    description: "Protect and extend your commercial roof’s life with expert coatings in Hilliard, Ohio.",
    image: coatings,
    tag: "Commercial"
  },
  {
    title: "Commercial Metal",
    description: "Strong, low-maintenance metal roofing solutions for Hilliard commercial facilities.",
    image: metal,
    tag: "Commercial"
  },
  {
    title: "Commercial Roof Repair",
    description: "Fast, reliable commercial roof repairs in Hilliard, Ohio to stop leaks and damage.",
    image: repairs,
    tag: "Commercial"
  }
];

const COLORS = {
  primary: '#3D3B6D',
  accent: '#b22335',
  border: '#e8e8ef',
  cardBg: '#f8f9fa',
  overlay: 'rgba(61, 59, 109, 0.65)'
};

const ScrollReveal = ({ children, delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const directionMap = {
    up: { y: 60 },
    left: { x: 60 },
    right: { x: -60 }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...directionMap[direction] }}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.7, delay }}
    >
      {children}
    </motion.div>
  );
};

const ParallaxImage = ({ src, alt }) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  return (
    <div ref={ref} className="position-relative overflow-hidden flex-shrink-0" style={{ height: '240px' }}>
      <motion.img
        src={src}
        alt={alt}
        className="w-100 h-100"
        style={{ objectFit: 'cover', y }}
      />
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background: `linear-gradient(180deg, ${COLORS.overlay} 0%, rgba(26,26,46,.45) 100%)`
        }}
      />
    </div>
  );
};

const ServiceCard = ({ service, index }) => {
  const isEven = index % 2 === 0;

  return (
    <motion.div whileHover={{ y: -10, scale: 1.02 }} className="h-100 d-flex flex-column w-100">
      <div
        className="h-100 position-relative overflow-hidden d-flex flex-column"
        style={{
          borderRadius: '16px',
          backgroundColor: COLORS.cardBg,
          border: `1px solid ${COLORS.border}`
        }}
      >
        <ParallaxImage src={service.image} alt={service.title} />

        <div className="position-absolute" style={{ top: '14px', left: '14px', zIndex: 2 }}>
          <span
            className="comm-badge"
            style={{
              background: isEven ? COLORS.accent : COLORS.primary
            }}
          >
            {service.tag}
          </span>
        </div>

        <div className="p-4 d-flex flex-column flex-grow-1">
          <h3 className=" m-0 mb-2 comm-card-title">
            {service.title}
          </h3>

          <p className="comm-card-desc flex-grow-1">
            {service.description}
          </p>

          <Link to="/commercial-roofing" className="comm-card-link mt-auto">
            Learn More
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="ms-2"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div
          className="position-absolute bottom-0 start-0"
          style={{
            height: '3px',
            width: '100%',
            background: `linear-gradient(90deg, ${isEven ? COLORS.accent : COLORS.primary}, transparent)`
          }}
        />
      </div>
    </motion.div>
  );
};

const RoofingSection = () => {
  return (
    <section className="cs-section roofing-commercial-section">
      <Container>
        <div className="">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <ScrollReveal direction="left">
                <div className="cs-badge">
                  <Badge text={"Commercial Roofing"} />
                </div>
              </ScrollReveal>

              <ScrollReveal direction="left" delay={0.1}>
                <h2 className="cs-title comm-title">
                  Hilliard, Ohio Commercial <span>Roofing Solutions</span>
                </h2>
              </ScrollReveal>

              <ScrollReveal direction="left" delay={0.2}>
                <p className="cs-desc comm-desc">
                  Professional commercial roofing services are designed to protect assets, reduce maintenance costs, and minimise business disruption.
                </p>
              </ScrollReveal>
            </div>

            <View />
          </div>
        </div>

        <Row className="g-4 cs-content">
          {SERVICES.map((service, index) => (
            <Col lg={4} md={6} key={index} className="d-flex flex-column">
              <ServiceCard service={service} index={index} />
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default RoofingSection;
